import { SearchFilterV4 } from "@/types/SearchFilter";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@components/ui/command";
import {
  ChevronRightIcon,
  LanguagesIcon,
  LightbulbIcon,
  MegaphoneIcon,
  MicIcon,
  SearchIcon,
  SmileIcon,
  TypeIcon,
  UserCircleIcon,
} from "lucide-react";
import { Dispatch, ReactElement, SetStateAction, useState } from "react";
import PageInsightTypes from "./partials/PageInsightTypes";
import PageChannelTypes from "./partials/PageChannelTypes";
import PageChannelNames from "./partials/PageChannelNames";
import PageSentiments from "./partials/PageSentiments";
import PageLanguages from "./partials/PageLanguages";
import PageMentionKinds from "./partials/PageMentionKinds";
import { FilterOptions } from "@/types/FilterOptions";
import { cn } from "@/lib/utils";
import { PopoverContentProps } from "@radix-ui/react-popover";

const PAGE = {
  INSIGHT_TYPES: "insight_types",
  CHANNEL_TYPE: "channel_type",
  CHANNEL_NAME: "channel_name",
  SENTIMENT: "sentiment",
  LANGUAGE: "language",
  MENTION_KIND: "mention_kind",
  FREE_TEXT: "free_text",
} as const;

type Page = (typeof PAGE)[keyof typeof PAGE];

export default function SearchFilterComponent({
  setIsFreeTextOpen,
  setIsReachOpen,
  filterOptions,
  className,
  onChange,
  children,
  value,
  ...rest
}: {
  onChange: (value: SearchFilterV4, clear?: boolean) => void;
  setIsFreeTextOpen: Dispatch<SetStateAction<boolean>>;
  setIsReachOpen: Dispatch<SetStateAction<boolean>>;
  value: Partial<SearchFilterV4>;
  filterOptions: FilterOptions;
  children: ReactElement;
} & PopoverContentProps) {
  const { t } = useLingui();

  // Local state
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<Page>();

  // Handlers
  function handleChangePage(newPage: Page | undefined) {
    setPage(newPage);
    setSearch("");
  }

  function handleGoToTop() {
    handleChangePage(undefined);
  }

  function handleOpenChange(newIsOpen: boolean) {
    if (newIsOpen) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      handleGoToTop();
    }
  }

  function handleChange(newValue: Partial<SearchFilterV4>) {
    onChange(newValue);
    setSearch("");
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger render={children} />
      <PopoverContent
        data-testid="filter-bar--container"
        align="end"
        className={cn("w-80 p-0", className)}
        {...rest}
      >
        <Command
          onKeyDown={(e) => {
            if (
              ((e.key === "Escape" && !search) ||
                (e.key === "Backspace" && !search)) &&
              page
            ) {
              e.preventDefault();
              e.stopPropagation();
              handleGoToTop();
            } else if (e.key === "Escape" && search) {
              e.preventDefault();
              e.stopPropagation();
              setSearch("");
            }
          }}
        >
          <CommandInput
            value={search}
            placeholder={t`Sök`}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              <Trans>Inga filter hittades</Trans>
            </CommandEmpty>

            {!page && (
              <CommandGroup>
                {(filterOptions.filter_insight_types?.length ?? 0) > 0 && (
                  <CommandItem
                    data-testid="filter-bar--insight-type"
                    onSelect={() => handleChangePage(PAGE.INSIGHT_TYPES)}
                  >
                    <LightbulbIcon />
                    <Trans>Insikter</Trans>
                    <ChevronRightIcon className="ml-auto" />
                  </CommandItem>
                )}

                <CommandItem
                  data-testid="filter-bar--channel-type"
                  onSelect={() => handleChangePage(PAGE.CHANNEL_TYPE)}
                >
                  <MicIcon />
                  <Trans>Medietyp</Trans>
                  <ChevronRightIcon className="ml-auto" />
                </CommandItem>

                <CommandItem
                  data-testid="filter-bar--channel-name"
                  onSelect={() => handleChangePage(PAGE.CHANNEL_NAME)}
                >
                  <UserCircleIcon />
                  <Trans>Källa</Trans>
                  <ChevronRightIcon className="ml-auto" />
                </CommandItem>

                <CommandItem
                  data-testid="filter-bar--sentiment"
                  onSelect={() => handleChangePage(PAGE.SENTIMENT)}
                >
                  <SmileIcon />
                  <Trans>Sentiment</Trans>
                  <ChevronRightIcon className="ml-auto" />
                </CommandItem>

                <CommandItem
                  data-testid="filter-bar--language"
                  onSelect={() => handleChangePage(PAGE.LANGUAGE)}
                >
                  <LanguagesIcon />
                  <Trans>Språk</Trans>
                  <ChevronRightIcon className="ml-auto" />
                </CommandItem>

                <CommandItem
                  data-testid="filter-bar--mention-kind"
                  onSelect={() => handleChangePage(PAGE.MENTION_KIND)}
                >
                  <TypeIcon />
                  <Trans>Typ av omnämnande</Trans>
                  <ChevronRightIcon className="ml-auto" />
                </CommandItem>

                <CommandItem
                  data-testid="filter-bar--min-reach-threshold"
                  onSelect={() => {
                    setIsReachOpen(true);
                    handleOpenChange(false);
                  }}
                >
                  <MegaphoneIcon />
                  <Trans>Räckvidd…</Trans>
                </CommandItem>

                <CommandItem
                  data-testid="filter-bar--freetext"
                  onSelect={() => {
                    setIsFreeTextOpen(true);
                    handleOpenChange(false);
                  }}
                >
                  <SearchIcon />
                  <Trans>Frisök…</Trans>
                </CommandItem>
              </CommandGroup>
            )}

            {page === PAGE.INSIGHT_TYPES && (
              <PageInsightTypes
                options={filterOptions.filter_insight_types}
                value={value.insight_types}
                onChange={handleChange}
                onBack={handleGoToTop}
              />
            )}

            {page === PAGE.CHANNEL_TYPE && (
              <PageChannelTypes
                options={filterOptions.filter_sources}
                value={value.channel_type}
                onChange={handleChange}
                onBack={handleGoToTop}
              />
            )}

            {page === PAGE.CHANNEL_NAME && (
              <PageChannelNames
                options={filterOptions.filter_channels}
                value={value.channel_name}
                onChange={handleChange}
                onBack={handleGoToTop}
              />
            )}

            {page === PAGE.SENTIMENT && (
              <PageSentiments
                options={filterOptions.filter_sentiments}
                value={value.sentiment}
                onChange={handleChange}
                onBack={handleGoToTop}
              />
            )}

            {page === PAGE.LANGUAGE && (
              <PageLanguages
                options={filterOptions.filter_languages}
                onChange={handleChange}
                value={value.language}
                onBack={handleGoToTop}
              />
            )}

            {page === PAGE.MENTION_KIND && (
              <PageMentionKinds
                value={value.mention_kind}
                onChange={handleChange}
                onBack={handleGoToTop}
              />
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
